import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline, Refresh, Search } from "@mui/icons-material";
import PageHeader from "../components/ui/PageHeader";
import OverviewMetrics from "../components/ui/OverviewMetrics";
import SoftwareLicenseDevicesDialog from "../components/SoftwareLicenseDevicesDialog";
import SoftwareLicenseDeliveryDialog from "../components/SoftwareLicenseDeliveryDialog";
import SoftwareLicenseDeleteDialog from "../components/SoftwareLicenseDeleteDialog";
import SoftwareLicenseFormDialog, { SoftwareLicenseFormValues } from "../components/SoftwareLicenseFormDialog";
import LicenseDeliveryGuide from "../components/LicenseDeliveryGuide";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import api from "../services/api";
import { useApp } from "../contexts/AppContext";
import { softwareLicenseNumber } from "../utils/softwareLicenseNumber";

import { SOFTWARE_PRODUCTS as products, SELF_SERVICE_PRODUCTS, softwareProductLabel } from "../utils/softwareProducts";
const readProductFilter = (value: string | null) => value && products.includes(value) ? value : "";
const empty = {
  licenseMode: "NODE_LOCKED",
  limitConcurrentUse: false,
  concurrentSeats: 1,
  leaseSeconds: 120,
  requireTpm: false,
  maxSeats: 1,
  offlineDays: 7,
  companyId: "",
  product: "NextCut",
  reference: "",
  edition: "",
  modules: [] as number[],
  managerVersion: null as number | null,
  softwareVersion: "",
  isPerpetual: false,
  expiresAt: "",
  updatesEnabled: false,
  updatesExpiresAt: "",
  serviceEnabled: false,
  serviceExpiresAt: "",
  serviceContract: "",
  isActive: true,
  notes: "",
};
type Form = SoftwareLicenseFormValues;
type License = Form & {
  id: string;
  licenseNumber?: number;
  updatedAt?: string;
  openEvents?: number;
  managerEdition?: { maxUsers: number; maxMachines: number } | null;
  company: { id: string; name: string };
  status: string;
  updatesStatus: string;
  serviceStatus: string;
};
const message = (e: any) =>
  e.response?.data?.error?.message || "Operazione non riuscita. Riprova.";
const formatDate = (date: string) =>
  date
    ? new Date(date).toLocaleDateString("it-IT", { timeZone: "UTC" })
    : "Senza scadenza";
const statusLabels: Record<string, string> = {
  ACTIVE: "Attiva",
  EXPIRED: "Scaduta",
  SUSPENDED: "Sospesa",
  DISABLED: "Non incluso",
};
const Status = ({ value }: { value: string }) => (
  <Chip
    size="small"
    variant="outlined"
    label={statusLabels[value] || value}
    color={
      value === "ACTIVE"
        ? "success"
        : value === "EXPIRED"
          ? "error"
          : value === "SUSPENDED"
            ? "warning"
            : "default"
    }
  />
);
const expiresSoon = (l: License) => {
  if (l.isPerpetual || l.status !== "ACTIVE" || !l.expiresAt) return false;
  const days = Math.ceil(
    (new Date(`${l.expiresAt.slice(0, 10)}T23:59:59Z`).getTime() - Date.now()) /
      86400000,
  );
  return days >= 0 && days <= 30;
};
export default function SoftwareLicensesPage() {
  const [searchParams] = useSearchParams();
  const { state } = useApp();
  const canManage = ["administrator", "reseller"].includes(
    state.auth.user?.role || "",
  );
  const [licenses, setLicenses] = useState<License[]>([]);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [deviceLicense, setDeviceLicense] = useState<License | null>(null);
  const [deliveryLicense, setDeliveryLicense] = useState<License | null>(null);
  const [deleteLicense, setDeleteLicense] = useState<License | null>(null);
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [product, setProduct] = useState(() => readProductFilter(searchParams.get("product")));
  const [status, setStatus] = useState(() => {
    const value = searchParams.get("status") || "";
    return ["ACTIVE", "SOON", "EXPIRED", "SUSPENDED", "ATTENTION"].includes(value) ? value : "";
  });
  useEffect(() => {
    const value = searchParams.get("status") || "";
    setStatus(["ACTIVE", "SOON", "EXPIRED", "SUSPENDED", "ATTENTION"].includes(value) ? value : "");
    setProduct(readProductFilter(searchParams.get("product")));
    setPage(0);
  }, [searchParams]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/software-licenses");
      setLicenses(response.data.data.licenses);
    } catch (e) {
      setError(message(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const start = async (license?: License) => {
    setEditing(license?.id || null);
    setDialogError("");
    const next = { ...empty, product: !license && product ? product : empty.product };
    if (license)
      Object.keys(empty).forEach((key) => {
        const value = license[key as keyof Form];
        (next as any)[key] = key === "modules" ? (Array.isArray(value) ? [...value] : []) : key.endsWith("At")
          ? String(value || "").slice(0, 10)
          : (value ?? "");
      });
    setForm(next);
    setOpen(true);
    setCompaniesLoading(true);
    try {
      // Fetch all company pages; dropdown must also work with more than 100 customers.
      const all: { id: string; name: string }[] = [];
      for (let page = 1; ; page++) {
        const response = await api.get("/companies", {
          params: { page, limit: 100 },
        });
        const batch = response.data.data.companies;
        all.push(...batch);
        if (batch.length < 100) break;
      }
      setCompanies(all);
    } catch (e) {
      setDialogError(message(e));
    } finally {
      setCompaniesLoading(false);
    }
  };
  const invalidSeats =
    !Number.isInteger(form.maxSeats) ||
    form.maxSeats < 1 ||
    form.maxSeats > 1000 ||
    ((form.licenseMode === "FLOATING" || form.limitConcurrentUse) &&
      (!Number.isInteger(form.concurrentSeats) ||
        form.concurrentSeats < 1 ||
        form.concurrentSeats > form.maxSeats)) ||
    (form.licenseMode === "FLOATING" &&
      (!Number.isInteger(form.leaseSeconds) ||
        form.leaseSeconds < 60 ||
        form.leaseSeconds > 600)) ||
    (form.licenseMode === "NODE_LOCKED" &&
      (!Number.isInteger(form.offlineDays) ||
        form.offlineDays < 1 ||
        form.offlineDays > 30));
  const save = async () => {
    if (invalidSeats || (form.product === "NextManager" ? !form.managerVersion : !form.edition.trim()) || (form.product === "NextFrame" && !form.modules?.length)) return;
    setSaving(true);
    setDialogError("");
    try {
      const data = {
        ...form,
        modules: form.product === "NextFrame" ? form.modules : [],
        managerVersion: form.product === "NextManager" ? form.managerVersion : null,
        expiresAt: form.isPerpetual ? null : form.expiresAt || null,
        updatesExpiresAt: form.updatesEnabled
          ? form.updatesExpiresAt || null
          : null,
        serviceExpiresAt: form.serviceEnabled
          ? form.serviceExpiresAt || null
          : null,
      };
      if (editing) await api.put(`/software-licenses/${editing}`, data);
      else await api.post("/software-licenses", data);
      setOpen(false);
      await load();
    } catch (e) {
      setDialogError(message(e));
    } finally {
      setSaving(false);
    }
  };
  const filtered = licenses
    .filter(
      (l) =>
        (!product || l.product === product) &&
        (!status ||
          (status === "SOON"
            ? expiresSoon(l)
            : status === "ATTENTION"
              ? ["EXPIRED", "SUSPENDED"].includes(l.status)
              : l.status === status)) &&
        [l.reference, l.company.name, softwareLicenseNumber(l.licenseNumber), String(l.licenseNumber || ''), l.edition, l.softwareVersion, l.product]
          .join(" ")
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
    )
    .sort(
      (a, b) =>
        a.company.name.localeCompare(b.company.name, "it") ||
        a.reference.localeCompare(b.reference, "it"),
    );
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1),
  );
  const changeStatus = (value: string) => {
    setStatus(value);
    setPage(0);
  };
  const metricValue = (n: number) => (loading || error ? "—" : n);
  return (
    <Stack spacing={3}>
      <PageHeader
        title="Licenze software"
        subtitle="Gestisci NextFrame, NextDrill, NextCut, NextCloud e NEXT Manager. Controlla scadenze, prepara le consegne e segui i PC autorizzati."
        actions={
          <>
            <Button
              startIcon={<Refresh />}
              onClick={() => void load()}
              disabled={loading}
            >
              Aggiorna
            </Button>
            {canManage && <Button component={RouterLink} to="/licenses/next-manager">Versioni NEXT Manager</Button>}
            {canManage && (
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => void start()}
              >
                Nuova licenza
              </Button>
            )}
          </>
        }
      />
      {notice && <Alert severity="success" onClose={() => setNotice("")}>{notice}</Alert>}
      {canManage && <LicenseDeliveryGuide />}
      <OverviewMetrics
        items={[
          {
            label: "Licenze totali",
            value: metricValue(licenses.length),
            detail: "Tutti i software",
            onClick: () => changeStatus(""),
            selected: status === "",
          },
          {
            label: "Attive",
            value: metricValue(
              licenses.filter((l) => l.status === "ACTIVE").length,
            ),
            detail: "Diritto d’uso valido",
            color: "success.main",
            onClick: () => changeStatus("ACTIVE"),
            selected: status === "ACTIVE",
          },
          {
            label: "In scadenza",
            value: metricValue(licenses.filter(expiresSoon).length),
            detail: "Entro i prossimi 30 giorni",
            color: "warning.main",
            onClick: () => changeStatus("SOON"),
            selected: status === "SOON",
          },
          {
            label: "Scadute o sospese",
            value: metricValue(
              licenses.filter((l) =>
                ["EXPIRED", "SUSPENDED"].includes(l.status),
              ).length,
            ),
            detail: "Richiedono attenzione",
            color: "error.main",
            onClick: () => changeStatus("ATTENTION"),
            selected: status === "ATTENTION",
          },
        ]}
      />
      <Paper variant="outlined" sx={{ p: 2, boxShadow: "none" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            size="small"
            label="Cerca licenze"
            placeholder="Cliente, numero licenza, riferimento o versione"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            label="Software"
            value={product}
            onChange={(e) => {
              setProduct(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Tutti i software</MenuItem>
            {products.map((p) => (
              <MenuItem key={p} value={p}>
                {softwareProductLabel(p)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Stato"
            value={status}
            onChange={(e) => changeStatus(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Tutti gli stati</MenuItem>
            {["ACTIVE", "SOON", "EXPIRED", "SUSPENDED", "ATTENTION"].map(
              (s) => (
                <MenuItem key={s} value={s}>
                  {s === "SOON"
                    ? "In scadenza (30 gg)"
                    : s === "ATTENTION"
                      ? "Scadute o sospese"
                      : statusLabels[s]}
                </MenuItem>
              ),
            )}
          </TextField>
          {!!(search || product || status) && (
            <Button
              onClick={() => {
                setSearch("");
                setProduct("");
                changeStatus("");
              }}
            >
              Azzera filtri
            </Button>
          )}
        </Stack>
      </Paper>
      {error && <Alert severity="error">{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 6 }}>
          <CircularProgress aria-label="Caricamento licenze" />
        </Box>
      ) : (
        !error && (
          <Paper sx={{ overflow: "hidden", boxShadow: "none" }}>
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Typography fontWeight={700}>Elenco licenze</Typography>
              <Typography variant="body2" color="text.secondary">
                {filtered.length} di {licenses.length} licenze · ordinate per
                cliente
              </Typography>
            </Box>
            <TableContainer>
              <Table sx={{ minWidth: 850 }}>
                <TableHead>
                  <TableRow>
                    {[
                      "Cliente / riferimento",
                      "Software",
                      "Diritto d’uso",
                      "Aggiornamenti",
                      "Assistenza",
                      "PC autorizzabili",
                      ...(canManage ? ["Azioni"] : []),
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered
                    .slice(
                      currentPage * rowsPerPage,
                      (currentPage + 1) * rowsPerPage,
                    )
                    .map((l) => (
                      <TableRow key={l.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>{l.company.name}</Typography>
                          <Typography variant="body2" fontWeight={700} color="primary.main">{softwareLicenseNumber(l.licenseNumber)}</Typography>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {l.reference}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {softwareProductLabel(l.product)}
                          </Typography>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                          >
                            {l.edition || "Edizione da completare"}
                            {l.product === "NextFrame" && <Typography component="span" variant="caption" display="block">Moduli: {(l.modules || []).join(", ") || "Da configurare"}</Typography>}
                            {l.product === "NextManager" && l.managerEdition && <Typography component="span" variant="caption" display="block">Utenti: {l.managerEdition.maxUsers} · Macchine: {l.managerEdition.maxMachines}</Typography>}
                            {l.softwareVersion
                              ? ` · v${l.softwareVersion}`
                              : ""}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Status value={l.status} />
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {l.isPerpetual
                              ? "Perpetua"
                              : formatDate(l.expiresAt)}{" "}
                            ·{" "}
                            {l.licenseMode === "FLOATING"
                              ? `Flottante (${l.concurrentSeats} contemporanei)`
                              : l.limitConcurrentUse
                                ? `Legata al PC (${l.concurrentSeats} alla volta)`
                                : "Legata al PC"}
                          </Typography>
                          {!!l.openEvents && (
                            <Typography variant="caption" display="block" color="warning.main">
                              {l.openEvents === 1 ? "1 segnalazione da controllare" : `${l.openEvents} segnalazioni da controllare`} nelle postazioni
                            </Typography>
                          )}
                          {expiresSoon(l) && (
                            <Typography variant="caption" color="warning.main">
                              In scadenza entro 30 giorni
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Status value={l.updatesStatus} />
                          {l.updatesEnabled && (
                            <Typography variant="caption" display="block">
                              {formatDate(l.updatesExpiresAt)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Status value={l.serviceStatus} />
                          {l.serviceEnabled && (
                            <Typography variant="caption" display="block">
                              {formatDate(l.serviceExpiresAt)}
                              {l.serviceContract && ` · ${l.serviceContract}`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button
                            sx={{ whiteSpace: "nowrap" }}
                            onClick={() => setDeviceLicense(l)}
                            aria-label={`Postazioni licenza ${l.reference}`}
                          >
                            Postazioni ({l.maxSeats})
                          </Button>
                        </TableCell>
                        {canManage && (
                          <TableCell>
                            <Stack spacing={.75} sx={{ minWidth: 145 }}>
                            {SELF_SERVICE_PRODUCTS.includes(l.product) && <Button
                              variant="contained"
                              disabled={l.status !== "ACTIVE"}
                              onClick={() => setDeliveryLicense(l)}
                              aria-label={`Consegna licenza ${l.reference}`}
                            >Consegna licenza</Button>}
                            <Button
                              onClick={() => void start(l)}
                              aria-label={`Modifica licenza ${l.reference}`}
                            >
                              Modifica
                            </Button>
                            <Button color="error" startIcon={<DeleteOutline />} onClick={() => setDeleteLicense(l)} aria-label={`Elimina licenza ${l.reference}`}>
                              Elimina
                            </Button>
                            </Stack>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell
                        colSpan={canManage ? 7 : 6}
                        align="center"
                        sx={{ py: 6 }}
                      >
                        <Typography fontWeight={600}>
                          Nessuna licenza trovata.
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1 }}
                        >
                          {search || product || status
                            ? "Modifica i filtri o azzera la ricerca."
                            : canManage
                              ? "Crea una licenza per assegnare un software a un cliente."
                              : "Qui appariranno le licenze assegnate alla tua azienda."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={currentPage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="Righe"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} di ${count}`
              }
              getItemAriaLabel={(type) =>
                type === "next" ? "Pagina successiva" : "Pagina precedente"
              }
            />
          </Paper>
        )
      )}
      <Alert severity="info" variant="outlined">
        <strong>Come leggere le licenze.</strong> Diritto d’uso, aggiornamenti e
        assistenza hanno scadenze indipendenti. Una licenza perpetua non include
        automaticamente aggiornamenti o assistenza. I PC autorizzabili indicano
        un limite, non le attivazioni già effettuate. NextControl si gestisce
        nella scheda della macchina.
      </Alert>
      {deviceLicense && (
        <SoftwareLicenseDevicesDialog
          license={deviceLicense}
          canManage={canManage}
          onClose={() => setDeviceLicense(null)}
        />
      )}
      {deliveryLicense && <SoftwareLicenseDeliveryDialog license={deliveryLicense} onClose={() => setDeliveryLicense(null)} />}
      {deleteLicense && <SoftwareLicenseDeleteDialog license={deleteLicense} onClose={() => setDeleteLicense(null)} onDeleted={() => { setNotice(`Licenza ${deleteLicense.reference} eliminata.`); setDeleteLicense(null); void load(); }} />}
      <SoftwareLicenseFormDialog
        licenseNumber={licenses.find(l => l.id === editing)?.licenseNumber}
        open={open} editing={!!editing} saving={saving} companiesLoading={companiesLoading}
        error={dialogError} companies={companies} form={form} onChange={setForm}
        onClose={() => { if (!saving) setOpen(false); }} onSave={() => void save()}
      />
    </Stack>
  );
}
